/**
 * Workstream Template library — Resource Plan Epic 13 (RP-13.1), M3.
 *
 * One template per Workstream Type: the Labor Category mix a sizing number is distributed
 * across, and the S/M/L/XL default hours behind the presets.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * SAVING CREATES A VERSION, AND THE UI SAYS SO
 * ──────────────────────────────────────────────────────────────────────────────
 * RP-13.1: "Templates are versioned. Changing a template never alters existing
 * Workstreams." An admin editing the Experience mix needs to understand they are not
 * retroactively restating plans already sent — so the save button says "Save as v3", the
 * version chip is always visible, and a template referenced by live Workstreams says how
 * many.
 *
 * The running mix total is shown while editing, exactly as the Rate Card does for the
 * Project Mix (RP-12.1), because "sums to 100%" is a save-time refusal and discovering it
 * only on submit is the worse experience.
 */

import { Plus, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useActivateWorkstreamTemplateVersion,
  useSaveWorkstreamTemplate,
  useWorkstreamTemplates,
} from "@/hooks/use-plan-composition";
import { useLaborCategories } from "@/hooks/use-rate-card";
import { cn } from "@/lib/utils";
import type { WorkstreamTemplateView } from "@/schemas/resource-plans";

/** The six types, in the canonical RP-D4 order with their display labels. */
const TYPE_LABEL: Record<string, string> = {
  Experience: "Experience",
  Workflow: "Workflow",
  Integration: "Integration",
  Foundation: "Foundation",
  Advisory: "Advisory",
  RunSupport: "Run / Support",
};

const SIZE_LABEL: Array<[keyof DraftSizes, string]> = [
  ["s", "S"],
  ["m", "M"],
  ["l", "L"],
  ["xl", "XL"],
];

interface DraftSizes {
  s: string;
  m: string;
  l: string;
  xl: string;
}

interface DraftLine {
  laborCategoryId: string;
  mixPercent: string;
}

interface Draft {
  description: string;
  sizes: DraftSizes;
  lines: DraftLine[];
}

/** Integer hundredths, never `Number()` on a percentage — see src/lib/rate-card.ts. */
function mixTotalBp(lines: readonly DraftLine[]): number {
  return lines.reduce((sum, l) => {
    const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(l.mixPercent.trim());
    if (match === null) return sum;
    const whole = match[1] ?? "0";
    const frac = (match[2] ?? "").padEnd(2, "0");
    return sum + Number(`${whole}${frac}`);
  }, 0);
}

function draftFrom(template: WorkstreamTemplateView): Draft {
  return {
    description: template.description ?? "",
    sizes: {
      s: template.sizeSHours,
      m: template.sizeMHours,
      l: template.sizeLHours,
      xl: template.sizeXlHours,
    },
    lines: template.lines.map((l) => ({
      laborCategoryId: l.laborCategoryId,
      mixPercent: l.mixPercent,
    })),
  };
}

export function WorkstreamTemplateLibrary() {
  const [showSuperseded, setShowSuperseded] = useState(false);
  const {
    data: templates,
    isLoading,
    isError,
    error,
    refetch,
  } = useWorkstreamTemplates(showSuperseded);
  const { data: categories } = useLaborCategories();
  const save = useSaveWorkstreamTemplate();
  const activate = useActivateWorkstreamTemplateVersion();

  const [editing, setEditing] = useState<{ type: string; draft: Draft } | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || templates === undefined) {
    return (
      <ErrorState
        message={
          error instanceof Error ? error.message : "Could not load the Workstream Templates."
        }
        onRetry={refetch}
      />
    );
  }

  const active = templates.filter((t) => t.active);
  const superseded = templates.filter((t) => !t.active);
  const categoryOptions = (categories ?? []).filter((c) => c.active);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Workstream Templates</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            One template per Workstream Type. Sizing a Workstream with a single number distributes
            its hours across these Labor Categories by their mix, then spreads each category evenly
            across the Workstream&rsquo;s months. Saving creates a new version — Workstreams already
            sized keep the version they were sized from.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSuperseded((v) => !v)}
          className="shrink-0"
        >
          {showSuperseded ? "Hide history" : "Show history"}
        </Button>
      </header>

      <div className="space-y-3">
        {Object.keys(TYPE_LABEL).map((type) => {
          const template = active.find((t) => t.type === type);
          const isEditing = editing?.type === type;

          return (
            <section key={type} className="rounded-lg border border-border bg-card p-4 shadow-xs">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-base font-semibold">
                      {TYPE_LABEL[type] ?? type}
                    </h3>
                    {template === undefined ? (
                      <span className="rounded-full bg-warning-100 px-2 py-0.5 text-xs font-semibold text-warning-950 dark:text-warning-400">
                        no template yet
                      </span>
                    ) : (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        v{template.version}
                      </span>
                    )}
                    {template !== undefined && template.workstreamCount > 0 && (
                      <span
                        className="rounded-full bg-info-100 px-2 py-0.5 text-xs font-medium text-info-700"
                        title="Workstreams sized from this version keep it even after you save a new one."
                      >
                        {template.workstreamCount} Workstream
                        {template.workstreamCount === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                  {template?.description !== null && template?.description !== undefined && (
                    <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                      {template.description}
                    </p>
                  )}
                  {template !== undefined && !isEditing && (
                    <p className="mt-2 font-mono text-sm text-foreground">{template.mixSummary}</p>
                  )}
                  {template !== undefined && !isEditing && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      S {template.sizeSHours} · M {template.sizeMHours} · L {template.sizeLHours} ·
                      XL {template.sizeXlHours} hours
                    </p>
                  )}
                  {template?.lines.some((l) => l.laborCategoryInactive) === true && (
                    <p className="mt-2 text-sm text-warning-950 dark:text-warning-400">
                      This template references a deactivated Labor Category. New sizings skip it and
                      the remaining shares renormalise — edit the mix to settle it deliberately.
                    </p>
                  )}
                </div>

                {isEditing ? (
                  <div className="flex shrink-0 gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      disabled={save.isPending || mixTotalBp(editing.draft.lines) !== 10_000}
                      onClick={() => {
                        save.mutate(
                          {
                            type,
                            description:
                              editing.draft.description.trim() === ""
                                ? null
                                : editing.draft.description.trim(),
                            sizeSHours: editing.draft.sizes.s,
                            sizeMHours: editing.draft.sizes.m,
                            sizeLHours: editing.draft.sizes.l,
                            sizeXlHours: editing.draft.sizes.xl,
                            lines: editing.draft.lines,
                          },
                          { onSuccess: () => setEditing(null) },
                        );
                      }}
                    >
                      Save as v{(template?.version ?? 0) + 1}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() =>
                      setEditing({
                        type,
                        draft:
                          template === undefined
                            ? {
                                description: "",
                                sizes: { s: "80.00", m: "240.00", l: "640.00", xl: "1600.00" },
                                lines: [],
                              }
                            : draftFrom(template),
                      })
                    }
                  >
                    {template === undefined ? "Create" : "Edit"}
                  </Button>
                )}
              </div>

              {isEditing && (
                <TemplateEditor
                  draft={editing.draft}
                  categories={categoryOptions}
                  onChange={(draft) => setEditing({ type, draft })}
                />
              )}

              {showSuperseded && superseded.some((t) => t.type === type) && (
                <div className="mt-4 border-t border-border pt-3">
                  <p className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                    Earlier versions
                  </p>
                  <ul className="space-y-1.5">
                    {superseded
                      .filter((t) => t.type === type)
                      .map((t) => (
                        <li key={t.id} className="flex items-center justify-between gap-3 text-sm">
                          <span className="min-w-0 truncate">
                            <span className="font-medium">v{t.version}</span>
                            <span className="ml-2 font-mono text-muted-foreground">
                              {t.mixSummary}
                            </span>
                            {t.workstreamCount > 0 && (
                              <span className="ml-2 text-muted-foreground">
                                · {t.workstreamCount} Workstream
                                {t.workstreamCount === 1 ? "" : "s"} still on it
                              </span>
                            )}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={activate.isPending}
                            onClick={() => activate.mutate(t.id)}
                            className="h-6 shrink-0 px-2 text-sm"
                          >
                            <RotateCcw className="mr-1 h-3 w-3" />
                            Reinstate
                          </Button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function TemplateEditor({
  draft,
  categories,
  onChange,
}: {
  draft: Draft;
  categories: ReadonlyArray<{ id: string; name: string }>;
  onChange: (draft: Draft) => void;
}) {
  const totalBp = mixTotalBp(draft.lines);
  const balanced = totalBp === 10_000;
  const used = new Set(draft.lines.map((l) => l.laborCategoryId));
  const available = categories.filter((c) => !used.has(c.id));

  return (
    <div className="mt-4 space-y-4 border-t border-border pt-4">
      <div>
        <Label className="text-sm">When to use this type</Label>
        <Textarea
          value={draft.description}
          onChange={(e) => onChange({ ...draft, description: e.target.value })}
          rows={2}
          className="mt-1 text-sm"
          placeholder="Shown inline in the Workstream Type picker, next to the mix summary."
        />
      </div>

      <div>
        <Label className="text-sm">Size preset hours</Label>
        <div className="mt-1 grid grid-cols-4 gap-2">
          {SIZE_LABEL.map(([key, label]) => (
            <div key={key}>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
              <Input
                inputMode="decimal"
                value={draft.sizes[key]}
                onChange={(e) =>
                  onChange({ ...draft, sizes: { ...draft.sizes, [key]: e.target.value } })
                }
                className="h-8 text-sm"
              />
            </div>
          ))}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Must be ordered S ≤ M ≤ L ≤ XL.</p>
      </div>

      <div>
        <div className="flex items-baseline justify-between">
          <Label className="text-sm">Labor Category mix</Label>
          <span
            className={cn(
              "font-mono text-sm font-semibold",
              balanced ? "text-success-700 dark:text-success-400" : "text-error-700",
            )}
          >
            {(totalBp / 100).toFixed(2)}%{balanced ? "" : " — must be exactly 100.00%"}
          </span>
        </div>

        <ul className="mt-2 space-y-1.5">
          {draft.lines.map((line, index) => (
            <li key={line.laborCategoryId} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-sm">
                {categories.find((c) => c.id === line.laborCategoryId)?.name ??
                  "Deactivated category"}
              </span>
              <Input
                inputMode="decimal"
                value={line.mixPercent}
                onChange={(e) => {
                  const next = [...draft.lines];
                  next[index] = { ...line, mixPercent: e.target.value };
                  onChange({ ...draft, lines: next });
                }}
                className="h-8 w-20 text-right text-sm"
              />
              <span className="text-sm text-muted-foreground">%</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground hover:text-error-700"
                onClick={() =>
                  onChange({ ...draft, lines: draft.lines.filter((_, i) => i !== index) })
                }
                aria-label="Remove this Labor Category"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>

        {available.length > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <Select
              value=""
              onValueChange={(id) =>
                onChange({
                  ...draft,
                  lines: [...draft.lines, { laborCategoryId: id, mixPercent: "0.00" }],
                })
              }
            >
              <SelectTrigger className="h-8 w-64 text-sm">
                <SelectValue placeholder="Add a Labor Category" />
              </SelectTrigger>
              <SelectContent>
                {available.map((c) => (
                  <SelectItem key={c.id} value={c.id} className="text-sm">
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Plus className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        )}

        <p className="mt-2 text-xs text-muted-foreground">
          Management overhead and QA belong in every template — a Workstream with neither is a
          Workstream nobody is running or testing.
        </p>
      </div>
    </div>
  );
}
